/**
 * Command K is one shared surface with three jobs (#1466):
 *
 * Go  - pinned/recent work, governed record search and navigation.
 * Do  - acts supplied by the current page or record.
 * Ask - hand the draft and current context to the ONE Copilot surface.
 *
 * This file binds console data to @solum/ui's interaction model. It deliberately
 * owns no dialog, keyboard/listbox implementation or second Copilot renderer.
 *
 * ── IT IS THE *APPLICATION* PALETTE, AND THE WORD IS LOAD-BEARING (#2328 §7) ─
 * ROOT ruling 74071 §5 gives this file two obligations it used to hold neither
 * of, and both are discharged in `palette/providers.ts` rather than here:
 *
 *  · THE SETTINGS REGISTRY IS UNREACHABLE FROM HERE. Not filtered — unreachable.
 *    Nothing in this module's transitive imports touches `../settings/`, so
 *    there is no settings provider, no `Open Settings` result and no flag that
 *    would add one. `palette/imports.census.test.ts` asserts it in both
 *    directions. #2328 §7: *"This exclusion occurs at provider/registry
 *    composition time, not as a string filter after results are returned."*
 *
 *  · ADMINISTRATIVE DOORS ARE NOT ASKED. `applicationProviders` classifies every
 *    search tool, catalogue entity, recent and pin BEFORE anything is queried,
 *    and `searchTools` below is what remains. The previous shape queried
 *    `access.search_users` and five siblings on every keystroke and discarded
 *    the rows at render time, which ruling §5 names as the defect: *"not
 *    queried, not merely not returned."*
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { buildNav } from '@solum/experience'
import type { McpToolSpec, Row } from '@solum/sdk'
import {
  Button, CommandPalette as CommandSurface, EntityIcon, commandIntent, matchesText,
  parseCommandQuery, rankCommandGroups, useDebouncedValue,
  type CommandPaletteGroup, type CommandPaletteItem,
} from '@solum/ui'
import {
  useSchema, useSemantics, useEntityRows, useEntityIdentities, useRecordIdentities, PAGE_SIZE,
} from '../data/queries'
import { useSolum } from '../data/client'
import { getAllRecents, recordRecent, useRecentsVersion } from './recents'
import { launchCopilot } from '../copilot/launcher'
import { usePaletteScope } from './paletteScope'
import { deriveSearchTools, runFederatedSearch } from './federatedSearch'
import { usePins } from './pins'
import { useRegistry } from '../data/registry'
import { consoleProfile, showsFleetSurfaces } from '../data/profile'
import s from './CommandPalette.module.css'
import { openActivityComposer } from './ActivityComposerHost'
import { activityContextFor, composeDoGroups } from './doGroups'
import { recordDestination } from './recordDestination'
import { elevateDestination } from './elevateDestination'
import { applicationProviders } from './palette/providers'
import { ctxFor } from '../copilot/brain'
import { usePaletteRecordActions } from '../record/EntityActions'

/** Fallback only when a deployment has registered no search tools. */
const SEARCH_ENTITIES = ['crm_opportunity', 'core_organization', 'core_person'] as const

const displayOf = (row: Row): string =>
  String(row.display_name ?? row.legal_name ?? row.name
    ?? (`${row.given_name ?? ''} ${row.family_name ?? ''}`.trim() || 'Record unavailable'))

/** Collapse provider-specific opening groups into the approved two-column
 * hierarchy. The provider identities return once a query is present. */
export function commandOpeningGroups(
  goGroups: readonly CommandPaletteGroup[],
  doGroups: readonly CommandPaletteGroup[],
): CommandPaletteGroup[] {
  const unique = (items: CommandPaletteItem[]) => items
    .filter((item, index) => items.findIndex((candidate) => candidate.id === item.id) === index)
    .slice(0, 8)
  const recall = unique(goGroups
    .filter((group) => group.kind === 'recall')
    .flatMap((group) => group.items))
  const create = unique(doGroups.flatMap((group) => group.items))
  return [
    { id: 'opening-recall', label: 'Recent & pinned', items: recall, kind: 'recall', testId: 'pal-opening-recall' },
    { id: 'opening-do', label: 'Create & do', items: create, kind: 'actions', testId: 'pal-opening-do' },
  ]
}

export function CommandPalette({ open, onOpenChange }: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const { client } = useSolum()
  const schema = useSchema()
  const semantics = useSemantics()
  const entityIdentities = useEntityIdentities()
  const scope = usePaletteScope()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const pins = usePins()
  const registry = useRegistry()
  const fleet = showsFleetSurfaces(consoleProfile(), registry.isInstalled)
  const [widened, setWidened] = useState(false)
  const [explicitScope, setExplicitScope] = useState<typeof scope>(null)
  const [candidate, setCandidate] = useState<CommandPaletteItem['record']>(undefined)
  const [rawQuery, setQuery] = useState('')
  const parsed = parseCommandQuery(rawQuery)
  const query = parsed.query

  useEffect(() => {
    if (!open) return
    setWidened(false)
    setExplicitScope(null)
    setCandidate(undefined)
    setQuery('')
  }, [open])

  const sourceScope = explicitScope ?? scope
  const scoped = sourceScope && !widened
  const selectionCount = sourceScope?.selectionCount?.() ?? 0
  const scopeRecords = useRecordIdentities(sourceScope?.recordId ? [{ type: sourceScope.entity, id: sourceScope.recordId }] : [])
  const candidateActions = usePaletteRecordActions(candidate?.entity, candidate?.id)
  const rawNavigation = useMemo(
    () => schema.data && semantics.data
      ? buildNav(schema.data, semantics.data, entityIdentities.data) : [],
    [entityIdentities.data, schema.data, semantics.data],
  )
  /* THE GOVERNED CLASSIFICATION, WHEN THE DEPLOYMENT HAS ONE (ruling §5).
     A lookup rather than a scan, and keyed off the schema rows the palette
     already holds — `describe_schema()` sends every column of
     `core.entity_type`, so the day the column lands this map starts answering
     with no second call and no further change here. */
  const governedEntityClass = useMemo(() => {
    const byKey = new Map((schema.data?.entities ?? []).map((entity) => [entity.key, entity.palette_class]))
    return (entity: string) => byKey.get(entity)
  }, [schema.data])
  const identityByType = useMemo(
    () => new Map((entityIdentities.data ?? []).map((identity) => [identity.entity_type, identity])),
    [entityIdentities.data],
  )
  const canOpenRecord = useCallback(
    (entity: string) => identityByType.get(entity)?.navigation_eligible === true,
    [identityByType],
  )
  const canOpenPaletteRecord = useCallback(
    (entity: string) => canOpenRecord(entity)
      || (fleet && entity === 'controlplane_deployment'),
    [canOpenRecord, fleet],
  )

  /* Registered search tools are the global search universe. There is no client
     list to keep in sync with modules, and every result still came through its
     owning governed door. */
  const tools = useQuery({
    queryKey: ['mcp-tools'],
    queryFn: () => client.describeTools().catch(() => null as McpToolSpec[] | null),
    staleTime: 60_000,
  })
  const rawSearchTools = useMemo(
    () => (schema.data && Array.isArray(tools.data) ? deriveSearchTools(tools.data, schema.data.entities) : []),
    [schema.data, tools.data],
  )
  const governedToolClass = useMemo(() => {
    const byName = new Map((Array.isArray(tools.data) ? tools.data : []).map((tool) => [tool.name, tool.paletteClass]))
    return (tool: string) => byName.get(tool)
  }, [tools.data])
  /* ── THE COMPOSED APPLICATION PROVIDER LIST (ruling 74071 §5) ─────────────
   *
   * Everything the palette reaches for passes through here BEFORE it is used:
   * the fan-out list, the entity catalogue, the recents and the pins. An
   * administrative subject never becomes a query — not a `search_*` fan-out, and
   * not the `titles_of` resolution the recents do below, which is itself a read
   * of the record ruling §5 says must not be queried.
   *
   * Recents are read here rather than beside their resolution because of that
   * second half: filtering after `useRecordIdentities` would have asked the
   * question already. */
  const recentsVersion = useRecentsVersion()
  const rawRecents = useMemo(
    () => getAllRecents().map((item) => ({ entity: item.entity, id: item.id })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- an external store, read per open and per change
    [open, recentsVersion],
  )
  const composed = useMemo(() => applicationProviders({
    tools: rawSearchTools,
    navigation: rawNavigation,
    recents: rawRecents,
    pins: pins.items,
    governedEntityClass,
    governedToolClass,
  }), [governedEntityClass, governedToolClass, pins.items, rawNavigation, rawRecents, rawSearchTools])
  const searchTools = composed.searchTools
  const navigation = composed.navigation

  const debounced = useDebouncedValue(query.trim(), 200)
  const federated = useQuery({
    queryKey: ['fed-search', debounced],
    enabled: open && parsed.filter !== 'actions' && parsed.filter !== 'ask' && !scoped && searchTools.length > 0 && debounced.length >= 2,
    queryFn: () => runFederatedSearch(client, searchTools, debounced),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })
  const federatedActive = parsed.filter !== 'actions' && parsed.filter !== 'ask' && !scoped && searchTools.length > 0 && query.trim().length >= 2

  /* The legacy pools exist only for a deployment with no registered search and
     for a scoped list that did not supply its own rows. They are never the
     default global result wall. */
  const legacyActive = parsed.filter !== 'actions' && parsed.filter !== 'ask' && !scoped && searchTools.length === 0 && query.trim().length >= 2
  const scopeNeedsPool = Boolean(parsed.filter !== 'actions' && parsed.filter !== 'ask' && scoped && sourceScope && !sourceScope.items)
  const poolOn = legacyActive || scopeNeedsPool
  const opportunities = useEntityRows('crm_opportunity', PAGE_SIZE, poolOn)
  const organizations = useEntityRows('core_organization', PAGE_SIZE, poolOn)
  const people = useEntityRows('core_person', PAGE_SIZE, poolOn)
  const rowsOf = useMemo<Record<string, Row[]>>(() => ({
    crm_opportunity: (opportunities.data ?? []) as Row[],
    core_organization: (organizations.data ?? []) as Row[],
    core_person: (people.data ?? []) as Row[],
  }), [opportunities.data, organizations.data, people.data])

  const openRecord = useCallback((entity: string, id: string, label = '') => {
    const elevate = elevateDestination(fleet, entity, id, label)
    if (elevate) {
      recordRecent(entity, id)
      void navigate({ to: elevate.to, params: elevate.params })
      return
    }
    if (!canOpenRecord(entity)) return
    recordRecent(entity, id)
    void navigate(recordDestination(entity, id))
  }, [canOpenRecord, fleet, navigate])

  /* Recents resolve by id, so a rename or a row outside the first page remains
     truthful.
     `open` re-reads the list each invocation — and `recentsVersion` re-reads it
     when the list itself changes, which #2325 made necessary: recents are the
     caller's governed `core.user_setting` row now, so on a cold load they
     arrive AFTER the first render rather than being synchronously present in
     localStorage. Keyed on `open` alone, a palette invoked before the settings
     read landed showed no recents and never recovered. Both keys live on
     `rawRecents` above, which is where the list is now read.

     `composed.recents`, not the raw list: a recent record of an administrative
     entity is one of the RECORDS ruling §5 excludes, and it must not reach
     `useRecordIdentities` either — that call is a governed `titles_of` read of
     exactly the row in question. */
  const recentRefs = useMemo(
    () => composed.recents.map((item) => ({ type: item.entity, id: item.id })),
    [composed.recents],
  )
  const recentRecords = useRecordIdentities(recentRefs)
  const recents = useMemo(() => recentRefs
    .filter((ref) => recentRecords.canNavigate(ref.type))
    .map((ref) => ({ ...ref, identity: recentRecords.identity(ref.type, ref.id) }))
    .filter((item) => recentRecords.query.isPending || item.identity.resolved)
    .map((item) => ({
      entity: item.type, id: item.id, label: item.identity.title,
      typeLabel: item.identity.typeLabel, iconKey: item.identity.iconKey,
    }))
    .slice(0, 8), [recentRecords, recentRefs])

  const goGroups = useMemo<CommandPaletteGroup[]>(() => {
    if (scoped && sourceScope) {
      const items = (sourceScope.items ?? (rowsOf[sourceScope.entity] ?? [])
        .map((row) => ({ id: String(row.id), label: displayOf(row) })))
        .filter((item) => matchesText(query, item.label))
        .slice(0, 40)
        .map((item) => ({
          id: `scope:${sourceScope.entity}:${item.id}`,
          label: item.label,
          detail: sourceScope.label,
          kind: 'record' as const,
          record: { entity: sourceScope.entity, id: item.id, label: item.label },
          onSelect: () => sourceScope.open ? sourceScope.open(item.id) : openRecord(sourceScope.entity, item.id, item.label),
        }))
      return [{
        id: 'scope', label: sourceScope.items ? sourceScope.label : `${sourceScope.label} - first ${PAGE_SIZE} loaded`,
        items, testId: 'pal-rows', kind: 'records',
      }]
    }

    const groups: CommandPaletteGroup[] = []
    const pinned = composed.pins
      .filter((pin) => pin.scope === 'navigation')
      .filter((pin) => pin.kind !== 'record' || !pin.entity || canOpenPaletteRecord(pin.entity))
      .filter((pin) => matchesText(query, pin.name, pin.type, pin.kind)).slice(0, query ? 20 : 6)
    if (pinned.length) groups.push({
      id: 'pinned', label: 'Pinned', testId: 'pal-pins', kind: 'recall',
      items: pinned.map((pin) => ({
        id: `pin:${pin.to}`, label: pin.name, detail: pin.type,
        kind: 'recall' as const,
        onSelect: () => { void navigate({ to: pin.to }) },
      })),
    })

    const recent = recents.filter((item) => matchesText(query, item.label, item.typeLabel)).slice(0, query ? 8 : 6)
    if (recent.length) groups.push({
      id: 'recent', label: 'Recent', testId: 'pal-recents', kind: 'recall',
      items: recent.map((item) => ({
        id: `recent:${item.entity}:${item.id}`, label: item.label, detail: item.typeLabel,
        icon: <EntityIcon iconKey={item.iconKey} />,
        kind: 'record' as const,
        record: { entity: item.entity, id: item.id, label: item.label },
        onSelect: () => openRecord(item.entity, item.id, item.label),
      })),
    })

    if (federatedActive) {
      for (const group of federated.data?.groups ?? []) {
        const items = group.hits.filter(() => Boolean(group.tool.entity) && canOpenPaletteRecord(group.tool.entity!)).map((hit) => ({
          id: `fed:${group.tool.name}:${hit.id}`,
          label: hit.label,
          detail: hit.detail || group.tool.label,
          icon: <EntityIcon iconKey={identityByType.get(group.tool.entity!)?.icon_key} />,
          kind: 'record' as const,
          record: { entity: group.tool.entity!, id: hit.id, label: hit.label },
          onSelect: () => openRecord(group.tool.entity!, hit.id, hit.label),
        }))
        if (items.length) groups.push({
          id: `fed:${group.tool.name}`, label: group.tool.label, items, testId: 'pal-fed-group', kind: 'records',
        })
      }
    }

    if (legacyActive) {
      const items = SEARCH_ENTITIES.filter(canOpenRecord).flatMap((entity) => (rowsOf[entity] ?? [])
        .filter((row) => matchesText(query, displayOf(row)))
        .slice(0, 40)
        .map((row) => ({
          id: `legacy:${entity}:${String(row.id)}`, label: displayOf(row),
          detail: identityByType.get(entity)?.singular_label ?? 'Record',
          icon: <EntityIcon iconKey={identityByType.get(entity)?.icon_key} />,
          kind: 'record' as const,
          record: { entity, id: String(row.id), label: displayOf(row) },
          onSelect: () => openRecord(entity, String(row.id), displayOf(row)),
        })))
      groups.push({
        id: 'legacy', label: `Records - first ${PAGE_SIZE} per type (no server search installed)`, items,
        testId: 'pal-legacy-records', kind: 'records',
      })
    }

    // The cockpit is a product destination, not a generic entity. It appears
    // only on a fleet console and only once the person searches for it; the
    // governed customer/environment hits above then route into the same stable
    // cockpit rather than the generic organization form.
    if (fleet && query.trim() && matchesText(query, 'Elevate Cockpit', 'Products', 'customers subscriptions environments')) {
      groups.unshift({
        id: 'elevate-cockpit', label: 'Products', testId: 'pal-elevate', kind: 'pages',
        items: [{
          id: 'go:elevate-cockpit', label: 'Elevate Cockpit',
          detail: 'Customers, subscriptions and environments',
          kind: 'page',
          onSelect: () => { void navigate({ to: '/ops/estate', search: {} }) },
        }],
      })
    }

    // The catalogue is a search result, never the opening wall of choices.
    if (query.trim()) {
      for (const group of navigation) {
        const items = group.entities
          .filter((entity) => matchesText(query, group.module, entity.label, entity.key))
          .map((entity) => ({
            id: `nav:${entity.key}`, label: entity.label, detail: group.module,
            icon: <EntityIcon iconKey={entity.iconKey} />,
            kind: 'page' as const,
            onSelect: () => { void navigate({ to: '/e/$entity', params: { entity: entity.key } }) },
          }))
        if (items.length) groups.push({ id: `nav:${group.module}`, label: group.module, items, kind: 'pages' })
      }
    }
    return groups
  }, [canOpenPaletteRecord, canOpenRecord, composed.pins, federated.data?.groups, federatedActive, fleet, identityByType, legacyActive, navigation, navigate, openRecord, query, recents, rowsOf, sourceScope, scoped])

  useEffect(() => {
    if (!candidate) return
    const stillVisible = goGroups.some((group) => group.items.some((item) =>
      item.record?.entity === candidate.entity && item.record.id === candidate.id))
    if (!stillVisible) setCandidate(undefined)
  }, [candidate, goGroups])

  const doGroups = useMemo<CommandPaletteGroup[]>(() => {
    const governedScope = scoped ? sourceScope : null
    const context = activityContextFor(
      governedScope,
      governedScope?.recordId
        ? scopeRecords.identity(governedScope.entity, governedScope.recordId).title
        : '',
      candidate,
    )
    const composedGroups = composeDoGroups({
      query,
      scope: scoped ? sourceScope : null,
      context,
      selectionCount,
      close: () => onOpenChange(false),
      // Keep Command Marble one of the two explicit doors into the shared
      // composer; the pure ranker receives behaviour, it does not own a door.
      openActivity: (request) => openActivityComposer(request),
    })
      .map((group) => ({
        ...group, kind: 'actions' as const,
        items: group.items.map((item) => ({ ...item, kind: 'action' as const })),
      }))
    return candidateActions.items.length
      ? [{ id: 'record-actions', label: `${candidate?.label ?? 'Record'} — actions`,
          kind: 'actions' as const, items: candidateActions.items }, ...composedGroups]
      : composedGroups
  }, [candidate, candidateActions.items, onOpenChange, query, scopeRecords, scoped, selectionCount, sourceScope])

  const askGroups = useMemo<CommandPaletteGroup[]>(() => {
    const clean = query.trim()
    const actionLabels = doGroups.flatMap((group) => group.items.map((item) => item.label))
    if (!clean || (parsed.filter !== 'ask' && commandIntent(clean, actionLabels) !== 'ask')) return []
    return [{ id: 'ask', label: 'Copilot', items: [{
      id: 'ask-copilot', label: `Ask: “${clean}”`,
      detail: sourceScope?.label ?? 'Current workspace', testId: 'pal-ask', kind: 'ask',
      onSelect: () => { launchCopilot({ query: clean, contextSnapshot: ctxFor(sourceScope, pathname) }) },
    }], kind: 'ask' }]
  }, [doGroups, parsed.filter, pathname, query, sourceScope])

  const allGroups = useMemo(() => [...goGroups, ...doGroups, ...askGroups], [askGroups, doGroups, goGroups])
  const activeGroups = useMemo(() => rankCommandGroups(
    query, allGroups, parsed.filter, doGroups.flatMap((group) => group.items.map((item) => item.label)),
  ), [allGroups, doGroups, parsed.filter, query])
  const opening = !rawQuery.trim() && !scoped
  const visibleGroups = useMemo(
    () => opening ? commandOpeningGroups(goGroups, doGroups) : activeGroups,
    [activeGroups, doGroups, goGroups, opening],
  )
  const placeholder = scoped ? `Find in ${sourceScope.label}, run an action, or ask…` : 'Search, run a command, or ask…'
  const empty = scoped ? 'Nothing in this context matched. Backspace widens to everywhere.' : 'Nothing reachable matched.'
  const federatedPending = federatedActive && (federated.isPending || federated.isFetching)
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
  const dictate = isMac ? 'fn fn to dictate' : 'Win+H to dictate'

  return open ? (
    <>
    <CommandSurface
      label="Command Marble"
      testId="command-palette"
      query={rawQuery}
      onQueryChange={setQuery}
      onClose={() => onOpenChange(false)}
      groups={visibleGroups}
      placeholder={placeholder}
      empty={empty}
      pending={federatedPending}
      split={opening}
      scopeHints={[
        { id: 'records', label: 'Records', shortcut: '@' },
        { id: 'actions', label: 'Actions', shortcut: '>' },
        { id: 'ask', label: 'Ask', shortcut: '?' },
        { id: 'pages', label: 'Pages' },
      ]}
      onActiveItemChange={(item) => {
        if (sourceScope?.recordId) setCandidate(undefined)
        else if (item?.record) setCandidate(item.record)
      }}
      onScopeItem={(item) => {
        if (!item.record) return
        setExplicitScope({ entity: item.record.entity, recordId: item.record.id, label: item.record.label })
        setWidened(false)
        setQuery('')
      }}
      onEscape={() => {
        if (parsed.filter) { setQuery(query); return true }
        if (explicitScope) { setExplicitScope(null); return true }
        if (scoped) { setWidened(true); return true }
        return false
      }}
      onInputKeyDown={(event) => {
        if (event.key === 'Backspace' && !query && scoped) {
          event.preventDefault()
          if (explicitScope) setExplicitScope(null)
          else setWidened(true)
        }
      }}
      context={scoped ? (
        <span className={s.scopechip} data-testid="pal-scope">
          <span>In: {sourceScope.label}</span>
          <Button size="sm" variant="ghost" aria-label="Widen scope" onClick={() => setWidened(true)}>×</Button>
        </span>
      ) : parsed.filter ? (
        <span className={s.scopechip} data-testid="pal-filter">
          <span>{parsed.filter}</span>
          <Button size="sm" variant="ghost" aria-label="Clear filter" onClick={() => setQuery(query)}>×</Button>
        </span>
      ) : undefined}
      footer={(
        <span data-testid="pal-foot">
          {scoped && `${selectionCount ? `${selectionCount} selected · ` : ''}scope: ${sourceScope.label} · backspace widens`}
          {!scoped && federatedActive && federated.data
            && `searched ${federated.data.sources - federated.data.dropped - federated.data.failed} of ${federated.data.sources} governed sources`}
          {!scoped && !federatedActive && 'records, actions, pages and Ask share this field · @ records · > actions · ? Ask'}
          <span className={s.dictate} title="Your device's built-in dictation types into this field."> · 🎤 {dictate}</span>
        </span>
      )}
    />
    {candidateActions.overlays}
    </>
  ) : null
}
